/**
 * IMPORTANT: This file is only for handling instore routes.
 * Other modules are now managed in the merchant-portal project.
 *
 * Authentication is already handled in merchant-portal - the implementation
 * here is legacy only and will be removed in the future.
 * Only URLs will be maintained.
 *
 * Permissions here are only for instore and use legacy permission_scopes.
 * New permissions are in permission_scopes_v2.
 *
 * If you need to modify login functionality, please consult with the
 * merchant-services team.
 */

import React, { useEffect } from 'react';
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
} from 'react-router-dom';

import Auth from '../Utils/Auth/auth';
import { useAuth } from '../Utils/Auth/use-auth';
import { isFeatureEnabled } from '../Utils/Feature/feature_flag';
import { ACCOUNT_MANAGER } from '../Utils/constants';
import AccessDenied from '../components/AccessDenied/AccessDenied';
import { useLoaderContext } from '../contexts/Loader/LoaderContext';
import { usePermissionContext } from '../contexts/Role/PermissionContext';
import { Loader } from '../ds';
import { getUser } from '../mockData';
import Benefits from './pages/Benefits';
import CreateAccount from './pages/CreateAccount';
import Home from './pages/Home/Home';
import Layout from './pages/Layout';
import LoaderBlack from './pages/Layout/loader-black-2x.gif';
import Login from './pages/Login';
import PasswordRecover from './pages/Login/PasswordRecover';
import MerchantHomePage from './pages/MerchantHome/MerchantHomePage';
import NotFoundPage from './pages/NotFound';
import { getHostAppUrl } from '../Utils/environment';
import { isMerchantEnterprise } from '../Utils/utils';
import { useLaunchDarkly } from '../contexts/LaunchDarkly/LDContext';
import InstoreCollectionPage from './pages/InstoreCollection';

const RedirectToLogIn = () => {
  if (isFeatureEnabled('enabled_new_portal_urls')) {
    window.location.href = `${getHostAppUrl()}/login`;
    return null; // No renderiza nada en la UI
  }

  return <Redirect to={{ pathname: '/login' }} />;
};

const RedirectToMe = () => {
  if (isMerchantEnterprise() && isFeatureEnabled('enabled_new_portal_urls')) {
    window.location.href = `${getHostAppUrl()}/merchant-enterprise-payments-report`;
    return null; // No renderiza nada en la UI
  }

  return <Redirect to={{ pathname: '/me' }} />;
};

const Routes = () => {
  const user = getUser();
  const auth = useAuth();
  const { instoreModuleV2Enabled } = useLaunchDarkly();

  const { merchant, employee } = Auth.userData();
  const { isLoaderOpen } = useLoaderContext();

  const { setOpenModalRoot, setHotjarIdentified, hotjarIdentified } =
    usePermissionContext();

  const isAccountManager = employee?.role?.name === ACCOUNT_MANAGER;

  const merchantSelected = JSON.parse(
    window.localStorage.getItem('merchantSelected')
  );

  useEffect(() => {
    if (auth.user || Auth.userLoggedIn()) {
      if (isFeatureEnabled('hotjar') && !hotjarIdentified) {
        setHotjarIdentified(true);
        const { employee_id, role, full_name } = employee;
        const { merchant_id: storeId, name: storeName, channels } = merchant;

        window.hj('identify', employee_id, {
          first_name: full_name,
          role: role.name,
          company: storeName,
          merchant_id: storeId,
          channels: channels.map(item => item.channel).join(' '),
        });
      }

      if (isAccountManager && merchantSelected === null) {
        setOpenModalRoot(true);
      }
    }
  }, [
    auth.user,
    employee,
    hotjarIdentified,
    merchant,
    merchantSelected,
    setOpenModalRoot,
    setHotjarIdentified,
  ]);

  const { menus, routes } = usePermissionContext();
  // if path is not login load permissions
  // if (window.location.pathname !== '/login') {
  //   loadPermissions();
  // }

  return (
    <Router>
      <Loader src={LoaderBlack} isOpen={isLoaderOpen} />
      <Switch>
        {!isFeatureEnabled('enabled_new_portal_urls') && (
          <Route
            exact
            path={'/login'}
            render={() => (Auth.userLoggedIn() ? <RedirectToMe /> : <Login />)}
          />
        )}
        <Route
          exact
          path={'/benefits'}
          render={() => (Auth.userLoggedIn() ? <RedirectToMe /> : <Benefits />)}
        />
        <Route
          exact
          path={'/create-account'}
          render={() =>
            Auth.userLoggedIn() ? <RedirectToMe /> : <CreateAccount />
          }
        />
        <Route
          exact
          path={'/passwordrecover'}
          render={() =>
            Auth.userLoggedIn() ? <RedirectToMe /> : <PasswordRecover />
          }
        />
        <Route
          exact
          path={'/'}
          render={() =>
            Auth.userLoggedIn() ? <RedirectToMe /> : <RedirectToLogIn />
          }
        />
        <Layout menus={menus} user={user}>
          <Switch>
            <Route
              exact
              path='/home'
              render={() => {
                if (!Auth.userLoggedIn()) return <RedirectToLogIn />;

                if (
                  isMerchantEnterprise() &&
                  isFeatureEnabled('enabled_new_portal_urls')
                ) {
                  window.location.href = `${getHostAppUrl()}/merchant-enterprise-payments-report`;

                  return null; // No renderiza nada en la UI
                }

                return instoreModuleV2Enabled ? (
                  <InstoreCollectionPage />
                ) : (
                  <Home />
                );
              }}
            />
            <Route
              exact
              path={'/merchant-panel'}
              render={() => {
                if (!Auth.userLoggedIn()) return <RedirectToLogIn />;

                return isAccountManager ? (
                  <MerchantHomePage />
                ) : (
                  <AccessDenied />
                );
              }}
            />
            {routes.map(route => {
              return (
                <Route
                  path={route.path}
                  exact={route.exact}
                  key={route.path}
                  render={() => {
                    if (!Auth.userLoggedIn()) return <RedirectToLogIn />;

                    return route.isAuthorized ? (
                      <route.component />
                    ) : (
                      <AccessDenied />
                    );
                  }}
                />
              );
            })}
            <Route
              path='*'
              render={() => {
                return Auth.userLoggedIn() ? (
                  <NotFoundPage />
                ) : (
                  <RedirectToLogIn />
                );
              }}
            />
          </Switch>
        </Layout>
      </Switch>
    </Router>
  );
};

export default Routes;
